// src/playerListComponents/PlayerFiltersEstadisticas.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Divider,
  InputAdornment
} from '@mui/material';
import { ExpandLess, ExpandMore, InsertChart, Add, Remove } from '@mui/icons-material';

/**
 * Ce composant propose des champs texte pour filtrer les statistiques d'un joueur.
 * Chaque champ correspond à un filtre ">= la valeur indiquée".
 * Par défaut, l'accordéon est replié (expanded=false).
 * Si isMobile est true, on ne l'affiche pas (même logique que PlayerFiltersMedidas). 
 */
const PlayerFiltersEstadisticas = ({
  isMobile,
  // Props pour chaque filtre de stats
  gamesFilter,
  setGamesFilter,
  minutesFilter,
  setMinutesFilter,
  pointsFilter,
  setPointsFilter,
  twoFGPercentFilter,
  setTwoFGPercentFilter,
  threeFGPercentFilter,
  setThreeFGPercentFilter,
  freeThrowPercentFilter,
  setFreeThrowPercentFilter,
  rebOffFilter,
  setRebOffFilter,
  rebDefFilter,
  setRebDefFilter,
  rebTotFilter,
  setRebTotFilter,
  assistsFilter,
  setAssistsFilter,
  foulsFilter,
  setFoulsFilter,
  blocksFilter,
  setBlocksFilter,
  stealsFilter,
  setStealsFilter,
  turnoversFilter,
  setTurnoversFilter,
  rankingFilter,
  setRankingFilter,
  forceExpanded = false
}) => {
  // ⚠️ TOUS LES HOOKS EN PREMIER - AVANT TOUTE CONDITION
  const { t } = useTranslation();
  // Pour ouvrir/fermer l'accordéon
  const [expanded, setExpanded] = useState(forceExpanded || true);

  // Refs pour gérer l'incrémentation/décrémentation continue
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Mise à jour de l'état expanded quand forceExpanded change
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  // Cleanup des intervals au démontage du composant
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Fonctions pour incrémenter/décrémenter avec gestion des décimales
  const incrementValue = (value, setter, step = 1) => {
    const numValue = parseFloat(value) || 0;
    const newValue = numValue + step;
    // Déterminer le nombre de décimales basé sur le step
    const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
    const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    setter(roundedValue.toString());
  };

  const decrementValue = (value, setter, step = 1) => {
    const numValue = parseFloat(value) || 0;
    const newValue = Math.max(0, numValue - step);
    // Déterminer le nombre de décimales basé sur le step
    const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
    const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    setter(roundedValue.toString());
  };

  // Fonctions pour gérer l'appui continu sur les boutons
  const startIncrement = (value, setter, step) => {
    // Premier incrément immédiat
    incrementValue(value, setter, step);

    // Après un délai de 500ms, commencer l'incrémentation continue
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setter(prev => {
          const numValue = parseFloat(prev) || 0;
          const newValue = numValue + step;
          const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
          const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
          return roundedValue.toString();
        });
      }, 100); // Répéter toutes les 100ms
    }, 500);
  };

  const startDecrement = (value, setter, step) => {
    // Premier décrément immédiat
    decrementValue(value, setter, step);

    // Après un délai de 500ms, commencer la décrémentation continue
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setter(prev => {
          const numValue = parseFloat(prev) || 0;
          const newValue = Math.max(0, numValue - step);
          const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
          const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
          return roundedValue.toString();
        });
      }, 100); // Répéter toutes les 100ms
    }, 500);
  };

  const stopContinuous = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Fonction pour gérer les changements d'input
  const handleInputChange = (setValue) => (e) => {
    const inputValue = e.target.value;
    // Permettre les nombres avec décimales
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  // Helper pour créer les props du bouton de décrémentation
  const getDecrementButtonProps = (value, setter, step) => ({
    onMouseDown: () => startDecrement(value, setter, step),
    onMouseUp: stopContinuous,
    onMouseLeave: stopContinuous,
    onTouchStart: () => startDecrement(value, setter, step),
    onTouchEnd: stopContinuous,
  });

  // Helper pour créer les props du bouton d'incrémentation
  const getIncrementButtonProps = (value, setter, step) => ({
    onMouseDown: () => startIncrement(value, setter, step),
    onMouseUp: stopContinuous,
    onMouseLeave: stopContinuous,
    onTouchStart: () => startIncrement(value, setter, step),
    onTouchEnd: stopContinuous,
  });

  // ⚠️ MAINTENANT ON PEUT FAIRE LES CONDITIONS ET RETURNS
  // Si on ne souhaite pas afficher le composant en vue mobile
  if (isMobile) {
    return null;
  }

  // Styles constants pour les boutons + et - (même style que PlayerFiltersMedidas)
  const decrementButtonStyle = {
    backgroundColor: '#5DADE2',
    color: 'white',
    borderRadius: '50%',
    width: 28,
    height: 28,
    padding: 0,
    marginLeft: 0.5,
    marginRight: -0.5,
    '&:hover': {
      backgroundColor: '#4A9BCE',
      color: 'white'
    },
    '& .MuiSvgIcon-root': {
      fontSize: 18
    }
  };

  const incrementButtonStyle = {
    backgroundColor: '#3B5998',
    color: 'white',
    borderRadius: '50%',
    width: 28,
    height: 28,
    padding: 0,
    marginRight: 0.5,
    marginLeft: -0.5,
    '&:hover': {
      backgroundColor: '#2D4373',
      color: 'white'
    },
    '& .MuiSvgIcon-root': {
      fontSize: 18
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* En-tête avec icône et bouton pour replier/déplier (seulement si pas forceExpanded) */}
      {!forceExpanded && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mb: 2,
              p: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <InsertChart sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('playerFiltersEstadisticas.title')}
            </Typography>
            <IconButton size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* Contenu repliable/dépliable */}
      <Collapse in={expanded}>
        {/* Grille responsive : 6 colonnes sur large, 3 sur moyen, 1 sur mobile */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 2,
            '@media (max-width: 1200px)': {
              gridTemplateColumns: 'repeat(3, 1fr)',
            },
            '@media (max-width: 600px)': {
              gridTemplateColumns: 'repeat(1, 1fr)',
            },
          }}
        >
          <TextField
            label={t('playerFiltersEstadisticas.labels.games')}
            variant="outlined"
            value={gamesFilter}
            onChange={handleInputChange(setGamesFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(gamesFilter, setGamesFilter, 1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(gamesFilter, setGamesFilter, 1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.minutes')}
            variant="outlined"
            value={minutesFilter}
            onChange={handleInputChange(setMinutesFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(minutesFilter, setMinutesFilter, 1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(minutesFilter, setMinutesFilter, 1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.points')}
            variant="outlined"
            value={pointsFilter}
            onChange={handleInputChange(setPointsFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(pointsFilter, setPointsFilter, 1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(pointsFilter, setPointsFilter, 1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.twoPointPercent')}
            variant="outlined"
            value={twoFGPercentFilter}
            onChange={handleInputChange(setTwoFGPercentFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(twoFGPercentFilter, setTwoFGPercentFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(twoFGPercentFilter, setTwoFGPercentFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.threePointPercent')}
            variant="outlined"
            value={threeFGPercentFilter}
            onChange={handleInputChange(setThreeFGPercentFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(threeFGPercentFilter, setThreeFGPercentFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(threeFGPercentFilter, setThreeFGPercentFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.freeThrowPercent')}
            variant="outlined"
            value={freeThrowPercentFilter}
            onChange={handleInputChange(setFreeThrowPercentFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(freeThrowPercentFilter, setFreeThrowPercentFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(freeThrowPercentFilter, setFreeThrowPercentFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.offensiveRebounds')}
            variant="outlined"
            value={rebOffFilter}
            onChange={handleInputChange(setRebOffFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(rebOffFilter, setRebOffFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(rebOffFilter, setRebOffFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.defensiveRebounds')}
            variant="outlined"
            value={rebDefFilter}
            onChange={handleInputChange(setRebDefFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(rebDefFilter, setRebDefFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(rebDefFilter, setRebDefFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.totalRebounds')}
            variant="outlined"
            value={rebTotFilter}
            onChange={handleInputChange(setRebTotFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(rebTotFilter, setRebTotFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(rebTotFilter, setRebTotFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.assists')}
            variant="outlined"
            value={assistsFilter}
            onChange={handleInputChange(setAssistsFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(assistsFilter, setAssistsFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(assistsFilter, setAssistsFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.personalFouls')}
            variant="outlined"
            value={foulsFilter}
            onChange={handleInputChange(setFoulsFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(foulsFilter, setFoulsFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(foulsFilter, setFoulsFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.blocks')}
            variant="outlined"
            value={blocksFilter}
            onChange={handleInputChange(setBlocksFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(blocksFilter, setBlocksFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(blocksFilter, setBlocksFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.steals')}
            variant="outlined"
            value={stealsFilter}
            onChange={handleInputChange(setStealsFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(stealsFilter, setStealsFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(stealsFilter, setStealsFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.turnovers')}
            variant="outlined"
            value={turnoversFilter}
            onChange={handleInputChange(setTurnoversFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(turnoversFilter, setTurnoversFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(turnoversFilter, setTurnoversFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />

          <TextField
            label={t('playerFiltersEstadisticas.labels.rating')}
            variant="outlined"
            value={rankingFilter}
            onChange={handleInputChange(setRankingFilter)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    {...getDecrementButtonProps(rankingFilter, setRankingFilter, 0.1)}
                    size="small"
                    sx={decrementButtonStyle}
                  >
                    <Remove />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...getIncrementButtonProps(rankingFilter, setRankingFilter, 0.1)}
                    size="small"
                    sx={incrementButtonStyle}
                  >
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiInputBase-input': {
                textAlign: 'center',
              }
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default PlayerFiltersEstadisticas;